// src/lib/padBuffer.js
//
// Axis-aligned square pad geometry from a point, sized in METERS. One square
// per selected point (never unioned). No turf dependency — the project only
// ships @turf/area + @turf/length, and a meter→degree conversion is exact
// enough for the Permian operating area (the authoritative meter-accurate
// buffering for the operating AOI happens server-side in PostGIS).

// Meters per degree of latitude (very nearly constant). Longitude scales by
// cos(lat) since meridians converge toward the poles.
const M_PER_DEG_LAT = 111320;

/**
 * Axis-aligned square centered on (lon, lat), with side length `sizeMeters`.
 * Returns a closed GeoJSON Polygon (ring's first == last vertex) in EPSG:4326.
 *
 * @param {number} lon
 * @param {number} lat
 * @param {number} sizeMeters  Side length in meters (e.g. 200).
 * @returns {{type:'Polygon', coordinates:number[][][]}}
 */
export function squarePolygon(lon, lat, sizeMeters) {
  const half = sizeMeters / 2;
  const dLat = half / M_PER_DEG_LAT;
  const dLon = half / (M_PER_DEG_LAT * Math.cos((lat * Math.PI) / 180));
  const ring = [
    [lon - dLon, lat - dLat],
    [lon + dLon, lat - dLat],
    [lon + dLon, lat + dLat],
    [lon - dLon, lat + dLat],
    [lon - dLon, lat - dLat], // close
  ];
  return { type: "Polygon", coordinates: [ring] };
}

/**
 * The point coordinates carried by a feature, normalizing Point and MultiPoint
 * so both behave identically everywhere in the builder (source-layer
 * eligibility, band-box hit-test, buffering). This is the one source of "the
 * points in a feature".
 *
 * The My Layers importer promotes points to MultiPoint geometry
 * (ogr2ogr -nlt PROMOTE_TO_MULTI), so a KMZ point layer arrives as MultiPoint;
 * without this, `geometry.type === 'Point'` checks silently drop it.
 *
 * @param {{geometry?: {type?: string, coordinates?: any}}} feature
 * @returns {Array<[number, number]>}  [] for any non-point geometry.
 */
export function extractPoints(feature) {
  const g = feature?.geometry;
  if (!g || !Array.isArray(g.coordinates)) return [];
  if (g.type === "Point") return [g.coordinates];
  if (g.type === "MultiPoint") return g.coordinates;
  return [];
}

/**
 * Lon/lat extent of a GeoJSON Polygon|MultiPolygon, in the { north, south,
 * east, west } shape the backend stores (for the pad-table bbox preview).
 *
 * @param {{type:string, coordinates:Array}} geometry
 * @returns {{north:number, south:number, east:number, west:number}|null}
 */
export function polygonBbox(geometry) {
  if (!geometry?.coordinates) return null;
  const rings =
    geometry.type === "MultiPolygon" ? geometry.coordinates.flat() : geometry.coordinates;
  let north = -Infinity, south = Infinity, east = -Infinity, west = Infinity;
  for (const ring of rings) {
    for (const [lon, lat] of ring) {
      if (lat > north) north = lat;
      if (lat < south) south = lat;
      if (lon > east) east = lon;
      if (lon < west) west = lon;
    }
  }
  if (!Number.isFinite(north)) return null;
  return { north, south, east, west };
}

/**
 * Test whether a [lon,lat] point falls inside a lng/lat bounding box.
 * Used by the band-box selection (tests source GeoJSON points, not
 * queryRenderedFeatures, so off-screen/decluttered points still select).
 *
 * @param {[number,number]} lngLat
 * @param {{west:number, south:number, east:number, north:number}} box
 */
export function pointInBox([lon, lat], box) {
  return lon >= box.west && lon <= box.east && lat >= box.south && lat <= box.north;
}

/**
 * Ray-casting (even-odd) point-in-polygon test. `ring` is an array of [lon,lat]
 * vertices and need NOT be explicitly closed — the wrap edge (last→first) is
 * handled. Returns true when [lon,lat] lies inside. Used by the builder's lasso
 * selection so the user can trace an arbitrary outline instead of a rectangle.
 *
 * @param {[number,number]} lngLat
 * @param {Array<[number,number]>} ring
 */
export function pointInRing([lon, lat], ring) {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    const intersects =
      (yi > lat) !== (yj > lat) &&
      lon < ((xj - xi) * (lat - yi)) / (yj - yi) + xi;
    if (intersects) inside = !inside;
  }
  return inside;
}
